import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import i18n from "@/i18n";
import { api } from "@/lib/api";
import { vereist } from "@jbnl/shared-ui";

const PERIODES =
  "/api/seizoenen/{seizoenId}/inschrijfperiodes/ondersteuners" as const;
const BESCHIKBAARHEID =
  "/api/inschrijfperiodes/{inschrijfperiodeId}/ondersteuners/{persoonId}/beschikbaarheid" as const;

/**
 * The API answers RFC 7807, so a refusal carries a Dutch sentence to show as-is - "de
 * inschrijving voor X is gesloten" rather than a save that failed for no stated reason. The
 * status code is the fallback for the cases that never reach a handler.
 */
function problem(error: unknown, fallback: string) {
  const title = (error as { title?: string } | undefined)?.title;
  return new Error(title ?? fallback);
}

/** The days an ondersteuner can serve on and the klassen they would like to serve in (ADR-0077). */
export type Keuze = { wedstrijddagIds: string[]; klasseIds: string[] };

export const beschikbaarheidKey = (
  inschrijfperiodeId: string,
  persoonId: string,
) => ["beschikbaarheid", inschrijfperiodeId, persoonId] as const;

export function useOndersteunerPeriodes(seizoenId: string | undefined) {
  return useQuery({
    queryKey: ["inschrijfperiodes", "ondersteuners", seizoenId ?? ""] as const,
    queryFn: !seizoenId
      ? skipToken
      : async () => {
          const { data, error } = await api.GET(PERIODES, {
            params: { path: { seizoenId } },
          });
          if (error)
            throw new Error(i18n.t("beschikbaarheid:errors.periodesOphalen"));
          return data ?? [];
        },
  });
}

export function useBeschikbaarheid(
  inschrijfperiodeId: string | undefined,
  persoonId: string | undefined,
) {
  return useQuery({
    queryKey: beschikbaarheidKey(inschrijfperiodeId ?? "", persoonId ?? ""),
    queryFn:
      !inschrijfperiodeId || !persoonId
        ? skipToken
        : async () => {
            const { data, error } = await api.GET(BESCHIKBAARHEID, {
              params: { path: { inschrijfperiodeId, persoonId } },
            });
            if (error)
              throw new Error(i18n.t("beschikbaarheid:errors.ophalen"));
            return data;
          },
  });
}

/**
 * The whole set, never a delta: a withdrawal is an id missing from it, which is how the server
 * tells the two apart. The error carries the API's own sentence, or a gated save would read as
 * "kon niet opslaan" and leave the ondersteuner guessing.
 */
export function useSetBeschikbaarheid(
  inschrijfperiodeId: string | undefined,
  persoonId: string | undefined,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (keuze: Keuze) => {
      const { data, error, response } = await api.PUT(BESCHIKBAARHEID, {
        params: {
          path: {
            inschrijfperiodeId: vereist(
              inschrijfperiodeId,
              "inschrijfperiodeId",
            ),
            persoonId: vereist(persoonId, "persoonId"),
          },
        },
        body: keuze,
      });
      if (!data)
        throw problem(
          error,
          i18n.t("beschikbaarheid:errors.opslaan", { status: response.status }),
        );
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: beschikbaarheidKey(inschrijfperiodeId ?? "", persoonId ?? ""),
      }),
  });
}
